import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useMember } from "./MemberShell";
import { toast } from "sonner";
import { Save } from "lucide-react";
import PhotoAvatar from "@/components/PhotoAvatar";

const inputCls = "w-full px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white";
const label = "block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5";

export default function MemberProfile() {
  const { member, refresh } = useMember();
  const location = useLocation();
  const [f, setF] = useState({ adres: "", tel1: "", tel2: "", gsm1: "", gsm2: "", email: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setF({
      adres: member.adres || "",
      tel1: member.tel1 || "",
      tel2: member.tel2 || "",
      gsm1: member.gsm1 || "",
      gsm2: member.gsm2 || "",
      email: member.email || "",
    });
  }, [member]);

  // Focus and scroll to the requested field if a hash is present (e.g. #adres)
  useEffect(() => {
    const hash = (location.hash || "").replace("#", "");
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        try { el.focus({ preventScroll: true }); } catch { /* ignore */ }
      }, 150);
    }
  }, [location.hash]);

  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const token = localStorage.getItem("member_token");
    try {
      await axios.patch(`${API}/member/me`, f, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Bilgileriniz güncellendi");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Güncelleme başarısız");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Profil</div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">İletişim Bilgilerim</h1>
        <p className="text-slate-600 mt-1 text-sm">Adres ve telefon bilgilerinizi buradan güncelleyebilirsiniz. Ad soyad ve T.C. kimlik yalnızca yönetim tarafından değiştirilebilir.</p>
      </div>

      <div className="crisp-card p-6">
        <h3 className="text-sm font-bold text-slate-900 mb-4">Profil Fotoğrafı</h3>
        <PhotoAvatar
          photoUrl={member.photo_url}
          name={member.ad_soyad}
          uploadUrl={`${API}/member/me/photo`}
          deleteUrl={`${API}/member/me/photo`}
          authHeader={{ Authorization: `Bearer ${localStorage.getItem("member_token") || ""}` }}
          onChange={() => refresh()}
          size={110}
        />
      </div>

      <div className="crisp-card p-6">
        <h3 className="text-sm font-bold text-slate-900 mb-4">Kimlik (yalnızca okuma)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <div className={label}>Ad Soyad</div>
            <div className="text-sm text-slate-900">{member.ad_soyad}</div>
          </div>
          <div>
            <div className={label}>T.C. Kimlik</div>
            <div className="text-sm text-slate-900 font-mono-nums">{member.tc_kimlik || "—"}</div>
          </div>
          <div>
            <div className={label}>Üyelik Tarihi</div>
            <div className="text-sm text-slate-900 font-mono-nums">{member.uyelik_tarihi || "—"}</div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="crisp-card p-6 space-y-5">
        <h3 className="text-sm font-bold text-slate-900">Düzenlenebilir Alanlar</h3>
        <label className="block">
          <span className={label}>Adres</span>
          <textarea id="adres" data-testid="profile-adres" rows={3} className={inputCls} value={f.adres} onChange={e => set("adres", e.target.value)} />
        </label>
        <label className="block">
          <span className={label}>E-posta</span>
          <input id="email" data-testid="profile-email" type="email" className={inputCls} value={f.email} onChange={e => set("email", e.target.value)} placeholder="ornek@eposta.com" />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <label className="block">
            <span className={label}>Telefon 1</span>
            <input id="tel1" data-testid="profile-tel1" className={inputCls} value={f.tel1} onChange={e => set("tel1", e.target.value)} />
          </label>
          <label className="block">
            <span className={label}>Telefon 2</span>
            <input id="tel2" data-testid="profile-tel2" className={inputCls} value={f.tel2} onChange={e => set("tel2", e.target.value)} />
          </label>
          <label className="block">
            <span className={label}>GSM 1</span>
            <input id="gsm1" data-testid="profile-gsm1" className={inputCls} value={f.gsm1} onChange={e => set("gsm1", e.target.value)} />
          </label>
          <label className="block">
            <span className={label}>GSM 2</span>
            <input id="gsm2" data-testid="profile-gsm2" className={inputCls} value={f.gsm2} onChange={e => set("gsm2", e.target.value)} />
          </label>
        </div>
        <div className="flex justify-end">
          <button data-testid="profile-save" disabled={busy} type="submit"
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 text-white font-semibold px-5 py-3 hover:bg-slate-800 disabled:opacity-60">
            <Save size={16}/> {busy ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
          </button>
        </div>
      </form>
    </div>
  );
}
